import { UPLOAD_BASE_URL } from "@/services/config";

/**
 * Shared media helpers.
 *
 * The deploy-time sync script (scripts/sync-mock-assets.mjs) and the runtime
 * image host (src/utils/mockImageHost.ts) MUST derive the SAME local file name
 * for a given CDN URL, so the file name is a stable djb2 hash of the whole URL.
 * Keep this exactly in sync with assetFileName() in the sync script.
 */
export function hashFileName(url: string, extension = "jpg"): string {
  let hash = 5381;
  for (let i = 0; i < url.length; i += 1) {
    hash = ((hash << 5) + hash + url.charCodeAt(i)) | 0;
  }
  return `${(hash >>> 0).toString(16).padStart(8, "0")}.${extension}`;
}

/** Public base every mirrored asset is served from (…/uploads). */
export function uploadRoot(): string {
  const base = UPLOAD_BASE_URL.replace(/\/+$/, "");
  return base || "/uploads";
}

/**
 * True when a URL points at one of Readdy's media CDNs (generated artwork,
 * uploaded branding, brand icons). Used by the runtime asset hosts to decide
 * whether a local mirror should take over.
 */
const READDY_CDN_RE =
  /(^|\/\/)([a-z0-9-]+\.)*(readdy\.ai|helloreaddy\.io|helloreaddy\.com|readdy-site\.link)\//i;

export function isReaddyCdnUrl(url: string): boolean {
  const value = (url || "").trim();
  return value.length > 0 && READDY_CDN_RE.test(value);
}

/**
 * File extension for a mirrored asset, taken from the URL path and defaulting
 * to "jpg". MUST mirror assetFileName() in scripts/sync-mock-assets.mjs so the
 * runtime and the deploy-time sync agree on the same local file name.
 */
export function assetExtension(url: string): string {
  const path = (url || "").split("?")[0].split("#")[0];
  const match = path.match(/\.(jpe?g|png|webp|gif|avif|svg)$/i);
  if (!match) return "jpg";
  const ext = match[1].toLowerCase();
  return ext === "jpeg" ? "jpg" : ext;
}